/** A tentativa permanece nesta aba até a OpenAI confirmar um resultado definitivo. */
export interface PendingResetAttempt {
  accountKey: string;
  idempotencyKey: string;
  creditId?: string;
}

type AttemptStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function storageKey(providerID: string, accountKey: string): string {
  return `oc-reset-credit-attempt:${encodeURIComponent(providerID)}:${encodeURIComponent(accountKey)}`;
}

function validAttempt(attempt: unknown, accountKey: string): attempt is PendingResetAttempt {
  if (!attempt || typeof attempt !== "object" || Array.isArray(attempt)) return false;
  const value = attempt as Partial<PendingResetAttempt>;
  return value.accountKey === accountKey && typeof value.idempotencyKey === "string" &&
    UUID_PATTERN.test(value.idempotencyKey) &&
    (value.creditId === undefined || typeof value.creditId === "string");
}

/** O servidor é a referência quando outra aba já iniciou o resgate desta conta. */
export function savePendingResetAttempt(
  providerID: string,
  attempt: PendingResetAttempt,
  storage: AttemptStorage = sessionStorage,
): PendingResetAttempt {
  if (!validAttempt(attempt, attempt.accountKey)) {
    throw new Error("A tentativa de redefinição recebida não é válida.");
  }
  storage.setItem(storageKey(providerID, attempt.accountKey), JSON.stringify(attempt));
  return attempt;
}

export function readPendingResetAttempt(
  providerID: string,
  accountKey: string,
  storage: AttemptStorage = sessionStorage,
): PendingResetAttempt | null {
  const raw = storage.getItem(storageKey(providerID, accountKey));
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    throw new Error("Não foi possível recuperar a tentativa de redefinição desta aba.");
  }
  if (!validAttempt(parsed, accountKey)) {
    throw new Error("Não foi possível recuperar a tentativa de redefinição desta aba.");
  }
  return parsed;
}

export function prepareResetAttempt(
  providerID: string,
  accountKey: string,
  storage: AttemptStorage = sessionStorage,
  newID: () => string = () => crypto.randomUUID(),
): PendingResetAttempt {
  const pending = readPendingResetAttempt(providerID, accountKey, storage);
  if (pending) return pending;
  const attempt: PendingResetAttempt = { accountKey, idempotencyKey: newID() };
  // Persistir antes do POST evita consumir outra redefinição após perda da resposta.
  return savePendingResetAttempt(providerID, attempt, storage);
}

export function completeResetAttempt(
  providerID: string,
  attempt: PendingResetAttempt,
  storage: AttemptStorage = sessionStorage,
): void {
  const saved = readPendingResetAttempt(providerID, attempt.accountKey, storage);
  if (saved?.idempotencyKey === attempt.idempotencyKey) {
    storage.removeItem(storageKey(providerID, attempt.accountKey));
  }
}
